import { usePriceVisibility, useUser, useUserSettings } from "@/composables/user";
import { LoginMarkerService } from "@/services/user/auth/login/loginMarkerService";
import { TokenService } from "@/services/user/auth/login/tokenService";
import { fetchUserSettings } from "@/services/user/settings";
import { AccountType, type User } from "@/services/user/userTypes";
import * as PostHog from "@/services/analytics/postHog";
import { Authentication, type AuthenticationPromise } from "@/services/user/auth/login/authenticationService";
import { addTokenToUrl, ApiClient, catch401 } from "@/services/apiClient";
import { createHomepageLink, router } from "@/router";
import { Facebook } from "@/services/user/auth/login/sso/FacebookLoginService";
import { logout as googleLogout } from "@/services/user/auth/login/sso/GoogleLoginService";
import { fetchUserBankInfo } from "@/services/user/bankInfo";
import { FallbackCurrencyId } from "@/services/currencies";
import { fetchUserAddress } from "@/services/user/profile";
import { useAppStore } from "@/composables";

export async function checkUserOnInitialPageLoad(): Promise<AuthenticationPromise> {
  if (Authentication.isInProgressOrDone()) {
    return Authentication.promise;
  } else {
    const token = await TokenService.get();
    if (token) {
      return Authentication.run({ token });
    } else {
      return null;
    }
  }
}

export async function login(email: string, password: string): Promise<AuthenticationPromise> {
  Authentication.reset();
  return Authentication.run({ email, password });
}

export async function logout(invalidateInApi = true): Promise<void> {
  const { user } = useUser();
  const { userSettings, userBankInfo } = useUserSettings();

  if (user.value) {
    if (user.value.type === AccountType.Facebook) {
      Facebook.logout();
    } else if (user.value.type === AccountType.Google) {
      googleLogout();
    }

    if (invalidateInApi) {
      const url = addTokenToUrl("profile/logout");
      await ApiClient.put(url).catch(catch401);
    }

    TokenService.delete();
    user.value = undefined;
    userSettings.value = undefined;
    userBankInfo.value = undefined;
    router.push(createHomepageLink());
  }

  Authentication.reset();
}

export async function setupUserInApp(user: User): Promise<void> {
  TokenService.store(user.token);
  LoginMarkerService.markLogin();

  PostHog.identifyUser(user);

  const userStore = useUser();
  userStore.user.value = user;

  usePriceVisibility().arePricesVisible.value = user.showPrices;

  const userSettingsStore = useUserSettings();

  await Promise.all([
    fetchUserSettings().then((settings) => (userSettingsStore.userSettings.value = settings)), //
    fetchUserBankInfo().then((bankInfo) => (userSettingsStore.userBankInfo.value = bankInfo)),
    fetchUserAddress().then((address) => (userSettingsStore.userAddress.value = address)),
  ]);

  const appStore = useAppStore();
  const userPreferredCurrencyId = userSettingsStore.userSettings.value?.displayCurrencyId ?? FallbackCurrencyId;
  appStore.setCurrencyId(userPreferredCurrencyId);
}
